import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';

import { MediaSubscriptionApiService } from '../../../../api/mediaSubscriptions/media-subscription.api.service';
import { BackfillRequestApiModel } from '../../../../api/mediaSubscriptions/models/backfill-request.api.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { SearchRangeModel } from '../../../Core/models/search-range.model';
import { MediaSubscription } from '../../../Shared/models/media-subscription.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { getMonitoringDateQueryInputTopicOptions } from '../../../Shared/models/monitoring-date-query-input-options';

@Component({
    selector: 'agpr-backfill-dialog',
    templateUrl: './backfill-dialog.component.html',
    styleUrls: ['./backfill-dialog.component.scss'],
    standalone: false
})
export class BackfillDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    messageSeverity = messageSeverityEnum;
    softLimit = 10000;

    @Input() subscription: MediaSubscription;
    @Input() allowedRange: SearchRangeModel;

    loading: boolean;

    dateFilterOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();

    dateQuery: DateQueryModel;
    selectedOption: DropdownOptionModel;

    private dateQuerySubject: Subject<DateQueryModel> = new Subject();
    private dateQuerySub: Subscription;
    readonly buttonTypes = ButtonTypesEnum;

    estimate: number;

    constructor(
        public coreDialogService: CoreDialogService,
        public mediaSubscriptionApiService: MediaSubscriptionApiService,
        public messageService: MessageService,
        public localizationService: LocalizationService
    ) {
        super(coreDialogService, 'Backfill');

        this.dateQuery = DateQueryModel.getLast24HoursQuery();
        this.selectedOption = this.dateFilterOptions[0];
    }

    ngOnInit(): void {
        if (!this.allowedRange) {
            this.allowedRange = new SearchRangeModel();
            this.allowedRange.to = new Date();
            this.allowedRange.from = moment().subtract(30, 'days').toDate();
        }
        this.estimateBackfillSize();
        this.dateQuerySub = this.dateQuerySubject.pipe(debounceTime(600)).subscribe(dateQuery => {
            this.dateQuery = dateQuery;
            this.estimateBackfillSize();
        });
    }

    getBackfillQuery(): BackfillRequestApiModel {
        const range = DateQueryModel.makeAbsoluteRange(this.dateQuery);
        return {
            backfillBeginDate: moment(range.absoluteRange.start).format(),
            backfillEndDate: moment(range.absoluteRange.end).format()
        };
    }

    updateDatequery(dateQuery: DateQueryModel) {
        this.dateQuerySubject.next(dateQuery);
    }

    estimateBackfillSize(): void {
        this.estimate = null;
        this.loading = true;
        this.mediaSubscriptionApiService.estimateBackfillCount(this.subscription.id, this.getBackfillQuery()).subscribe({
            next: (count) => {
                this.estimate = count;
                this.loading = false;
            },
            error: () => this.loading = false
        });
    }

    /**
     * Action when the user clicks the "OK" button
     */
    ok(): void {
        this.mediaSubscriptionApiService.requestBackfill(
            this.subscription.id,
            this.getBackfillQuery()
        ).subscribe({
            next: () => {
                this.closeDialog(true);
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'MediaSubscriptions.backfill.request.success.heading',
                    detail: this.localizationService.transform(
                        'MediaSubscriptions.backfill.request.success.message',
                        { name: this.subscription.name }
                    )
                });
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'MediaSubscriptions.backfill.request.error.heading',
                    detail: this.localizationService.transform(
                        'MediaSubscriptions.backfill.request.error.message',
                        { name: this.subscription.name }
                    )
                });
            }
        });
    }

    ngOnDestroy(): void {
        if (this.dateQuerySub) {
            this.dateQuerySub.unsubscribe();
        }
    }

    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog(false);
    }
}
